'use client';

import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Icon } from '../icons';
import styles from './ThemeToggle.module.css';

interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'button' | 'switch';
  /** Button variant only: hide the text label and show just the icon */
  iconOnly?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className,
  size = 'md',
  variant = 'button',
  iconOnly = false,
}) => {
  const { theme, toggleTheme } = useTheme();

  const toggleClasses = [
    styles.toggle,
    styles[size],
    styles[variant],
    iconOnly && variant === 'button' && styles.iconOnly,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (variant === 'switch') {
    return (
      <label className={toggleClasses}>
        <input
          type="checkbox"
          checked={theme === 'dark'}
          onChange={toggleTheme}
          className={styles.switchInput}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        />
        <span className={styles.switchSlider}>
          <span className={styles.switchIcon}>
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </span>
        </span>
        <span className={styles.switchLabel}>
          {theme === 'dark' ? 'Dark' : 'Light'} Mode
        </span>
      </label>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      className={toggleClasses}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      {!iconOnly && (
        <span className={styles.buttonLabel}>
          {theme === 'dark' ? 'Light' : 'Dark'} Mode
        </span>
      )}
    </button>
  );
};
